import { Component } from '@nicolas-riciotti/light2';

export default class BlockReveal extends Component {
  created() {
    this.toggle = this.toggle.bind(this);
    this._onDown = this._onDown.bind(this);
    this._onMove = this._onMove.bind(this);
    this._onUp = this._onUp.bind(this);
    this.opened = false;
    this.dragging = false;
    this._suppressClick = false;
  }

  attached() {

    this.$button = this.querySelector('[data-reveal-button]');
    this.$panel = this.querySelector('[id$="-panel"]');

    if (!this.$button) return;

    this.$button.addEventListener('click', this.toggle);

    // Drag-to-reveal: grab the panel and swipe it open/closed.
    if (this.$panel) {
      this.$panel.style.touchAction = 'pan-y'; // keep vertical page scroll, capture horizontal
      this.$panel.style.cursor = 'grab';
      // Prevent native image dragging inside the reveal panel
      this.$panel.querySelectorAll('img')?.forEach((image) => {
        image.setAttribute('draggable', 'false');
        image.style.userDrag = 'none';
        image.style.webkitUserDrag = 'none';
      });
      
      this.$panel.addEventListener('pointerdown', this._onDown);
    }

    this.syncAccessibilityState();
  }

  detached() {
    this.$button?.removeEventListener('click', this.toggle);
    this.$panel?.removeEventListener('pointerdown', this._onDown);
    window.removeEventListener('pointermove', this._onMove);
    window.removeEventListener('pointerup', this._onUp);
    clearTimeout(this._suppressClickTimer);
  }

  _currentTranslateX() {
    const t = getComputedStyle(this.$panel).transform;
    if (!t || t === 'none') return 0;
    try {
      return new DOMMatrixReadOnly(t).m41;
    } catch (e) {
      return 0;
    }
  }

  _onDown(event) {
    if (event.button != null && event.button !== 0) return;
    if (!this.$panel) return;

    // Do not start drag behavior when clicking the reveal button
    if (event.target.closest('[data-reveal-button]')) return;

    this.dragging = true;
    this._moved = 0;
    this._startPointerX = event.clientX;
    this._openX = this.$panel.offsetWidth || 1;
    this._startX = this._currentTranslateX();
    this._currentX = this._startX;

    this.$panel.style.transition = 'none';
    this.$panel.style.cursor = 'grabbing';
    this.style.userSelect = 'none';
    this.$panel.setPointerCapture?.(event.pointerId);

    window.addEventListener('pointermove', this._onMove);
    window.addEventListener('pointerup', this._onUp);
  }

  _onMove(event) {
    if (!this.dragging) return;

    const delta = event.clientX - this._startPointerX;
    this._moved = Math.max(this._moved, Math.abs(delta));

    let x = this._startX + delta;
    x = Math.max(0, Math.min(this._openX, x));
    this._currentX = x;

    if (this._moved > 4) event.preventDefault(); // stop text/image selection once dragging
    this.$panel.style.transform = `translateX(${x}px)`;
  }

  _onUp() {
    if (!this.dragging) return;
    this.dragging = false;

    window.removeEventListener('pointermove', this._onMove);
    window.removeEventListener('pointerup', this._onUp);

    this.$panel.style.cursor = 'grab';
    this.style.userSelect = '';

    // Restore the panel's CSS transition, then hand control back to the .is-opened class
    // so it animates from the dragged position to the snapped position.
    this.$panel.style.transition = '';
    void this.$panel.offsetWidth; // reflow: commit the dragged position as the animation start
    this.$panel.style.transform = '';

    // A tap (no real drag) is left to the button's own click handler.
    if (this._moved < 6) return;

    // Real drag: snap based on progress, and swallow the click the browser
    // synthesizes on release so it doesn't immediately toggle the state back.
    this._suppressClick = true;
    clearTimeout(this._suppressClickTimer);
    this._suppressClickTimer = setTimeout(() => { this._suppressClick = false; }, 350);

    const progress = this._currentX / this._openX;
    this.opened = progress > 0.5;
    this.classList.toggle('is-opened', this.opened);
    this.syncAccessibilityState();
  }

  toggle() {
    // Ignore the click that follows a drag (see _onUp).
    if (this._suppressClick) {
      this._suppressClick = false;
      return;
    }

    this.opened = !this.opened;

    this.classList.toggle('is-opened', this.opened);

    this.syncAccessibilityState();
  }

  syncAccessibilityState() {
    if (!this.$button) return;

    this.$button.setAttribute('aria-expanded', String(this.opened));

    if (this.opened) {
      this.$button.setAttribute('aria-label', 'Hide prompt');
      this.$panel?.setAttribute('aria-hidden', 'true');
    } else {
      this.$button.setAttribute('aria-label', 'Reveal prompt');
      this.$panel?.setAttribute('aria-hidden', 'false');
    }
  }
}

customElements.define('block-reveal', BlockReveal);
